import { useQueryClient } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ExecutionOutputTableCell } from '@/features/executions/components/ExecutionOutputTableCell';
import { buildTestRunOutputValues } from '@/features/executions/components/executionOutputTableCellUtils';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { updateTestRun } from '@/features/executions/data/testRuns';
import { buildTestRunParentInvalidations } from '@/features/executions/queries/executionInvalidationFilters';
import { testRunsGridConfig } from '@/features/executions/queries/executionsConfig';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  CLONE_META_DETAILS_LINK_SX as EXECUTION_DETAILS_LINK_SX,
  STATUS_META_TIMESTAMP_CONTENT_SX,
  STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX,
  TableBreakdownStack,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import {
  buildExecutionRunDetailPath,
  buildResultsFileDetailPath,
  buildSuiteDetailPath,
  buildTestDetailPath,
  buildTestRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import {
  buildTagColorByName,
  mergeTagColorByName,
  resolveEntityTags,
  resolveTagColor,
} from '@/features/shared/tags';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { patchQueryResultCaches } from '@/shared/data/queryResultCache';
import {
  gridQueryKey,
  gridPaths,
  invalidateGridQuery,
  invalidateTestRunDetail,
  invalidateTestRuns,
  queryKeys,
  useInvalidateQueries,
  useTagOptions,
} from '@/shared/hooks';
import { formatDuration } from '@/ui';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { getStatusIconComponent, RunLinkCell, TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  Box,
  type DataGridProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridSortModel,
  resolveStatusChipTone,
} from '@/ui/primitives';
import {
  DescriptionOutlinedIcon,
  EventOutlinedIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  TimerOutlinedIcon,
} from '@/ui/primitives/icons';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestRunDto } from '@flowqa/shared';
import type { QueryKey } from '@tanstack/react-query';

type TestRunsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  queryKey?: QueryKey;
  initialData?: QueryResult<TestRunDto>;
  onRowsChange?: (rows: TestRunDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: TestRunDto[] } | null) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  disableLinks?: boolean;
  search?: string;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  tagColorByName?: Partial<Record<string, TagColor>>;
  tagOptionsOverride?: TagOption[];
  hideNameColumn?: boolean;
  hideDetailsColumn?: boolean;
  hideSuiteNameInTestColumn?: boolean;
  runColumnFirst?: boolean;
  showPaginationSkeletonWhenRowsVisible?: boolean;
};

const TestRunsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = true,
  disableColumnMenu = true,
  showToolbar = true,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'testRun' }),
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  initialData,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  disableLinks = false,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  tagColorByName = {},
  tagOptionsOverride,
  hideNameColumn = false,
  hideDetailsColumn = false,
  hideSuiteNameInTestColumn = false,
  runColumnFirst = false,
  showPaginationSkeletonWhenRowsVisible = false,
}: TestRunsTableProps): ReactElement => {
  const columnWidths = tableColumnWidths.testRuns;
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { groupErroredWithFailed, preferFileNameOverDisplayName } = useProjectStatusPresentation();
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const [editTarget, setEditTarget] = useState<TestRunDto | null>(null);
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    updateRowTagsColorPendingId,
  } = useTestRunsTableRowActions({ selectedProjectId });
  const editEditor = useDetailEditEditor<TestRunDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Test run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTarget) return;
      const updated = await updateTestRun(
        { id: editTarget.id, tags, status, timestamp, durationMs },
        editTarget.organisationId ?? selectedOrganisationId ?? null,
      );
      patchQueryResultCaches(
        queryClient,
        [
          {
            queryKey: queryKeys.testRuns(
              selectedProjectId ?? null,
              editTarget.organisationId ?? null,
            ),
          },
          { queryKey: gridQueryKey(gridPaths.testRuns) },
        ],
        updated,
      );
      const invalidations = [
        ...buildTestRunParentInvalidations(editTarget),
        invalidateTestRuns(selectedProjectId ?? null, editTarget.organisationId ?? null),
        invalidateTestRunDetail(editTarget.id, editTarget.organisationId ?? null),
        invalidateGridQuery(gridPaths.testRuns),
      ];
      await invalidateQueries(invalidations);
    },
  });
  const resolvedTagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const resolvedTagColorByName = useMemo(
    () => buildTagColorByName(resolvedTagOptions),
    [resolvedTagOptions],
  );
  const mergedTagColorByName = useMemo(
    () => mergeTagColorByName(resolvedTagColorByName, tagColorByName),
    [resolvedTagColorByName, tagColorByName],
  );
  const columns = useMemo<Array<GridColDef<TestRunDto>>>(
    () => [
      {
        field: 'name',
        headerName: 'Test',
        flex: columnWidths.name.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-name-cell',
        valueGetter: (_value, row) =>
          preferFileNameOverDisplayName
            ? (row.testName ?? row.name ?? row.testId)
            : (row.displayName ?? row.testName ?? row.name ?? row.testId),
        renderCell: (params) => {
          const primarySuite = !hideSuiteNameInTestColumn
            ? preferFileNameOverDisplayName
              ? (params.row.suiteName ?? null)
              : (params.row.groupPath?.[0] ?? params.row.suiteName ?? null)
            : null;
          const testHref = disableLinks
            ? null
            : buildTestDetailPath(params.row.testSlug ?? params.row.testId);
          const suiteHref = disableLinks ? null : buildSuiteDetailPath(params.row.suiteSlug);
          const tags = resolveEntityTags(params.row);
          const resolvedTags = tags.map((tag) => ({
            label: tag,
            tone: resolveTagColor(tag, mergedTagColorByName),
          }));
          return (
            <TableCellContainer gap="none">
              <TableNameCell
                primary={{
                  label: preferFileNameOverDisplayName
                    ? (params.row.testName ?? params.row.name ?? params.row.testId)
                    : (params.row.displayName ??
                      params.row.testName ??
                      params.row.name ??
                      params.row.testId),
                  ...(testHref
                    ? {
                        href: testHref,
                        onClick: () => {
                          setSelectedProjectId(params.row.projectId);
                        },
                      }
                    : {}),
                }}
                secondary={
                  primarySuite
                    ? {
                        label: primarySuite,
                        ...(suiteHref
                          ? {
                              href: suiteHref,
                              onClick: () => {
                                setSelectedProjectId(params.row.projectId);
                              },
                            }
                          : {}),
                      }
                    : null
                }
                tags={resolvedTags}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'runNumber',
        headerName: getDetailLabel('run'),
        flex: columnWidths.run.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-run-link-cell',
        renderCell: (params) => {
          const runLabel =
            typeof params.row.runNumber === 'number' ? `#${params.row.runNumber}` : 'View';
          const href =
            buildTestRunPath(params.row.testSlug ?? params.row.testId, params.row.runNumber) ??
            '/tests';
          return (
            <TableCellContainer gap="none">
              <RunLinkCell
                label={runLabel}
                href={disableLinks ? null : href}
                latest={params.row.isLatest === true}
                onClick={() => {
                  setSelectedProjectId(params.row.projectId);
                }}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'status',
        headerName: getDetailLabel('status'),
        flex: columnWidths.status.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-status-meta-cell',
        renderCell: (params) => {
          const displayStatus = resolveProjectDisplayStatus(
            params.row.status,
            groupErroredWithFailed,
          );
          const statusTone = resolveStatusChipTone(displayStatus);
          const statusLabel =
            resolveProjectDisplayStatusLabel(params.row.status, groupErroredWithFailed) || '-';
          const testRunHref = disableLinks
            ? null
            : buildTestRunPath(params.row.testSlug ?? params.row.testId, params.row.runNumber);
          const durationLabel = formatDuration(params.row.durationMs ?? null);
          const timestamp = params.row.timestamp;
          const timestampLabel = formatRelativeTime(timestamp);
          const timestampTitle = formatDate(timestamp, { includeTime: true });
          const resolvedTimestampTitle = timestampTitle || timestamp.toISOString();
          return (
            <TableCellContainer gap="none">
              <TableStatusMetaContent
                statusLabel={statusLabel}
                statusHref={testRunHref}
                onStatusClick={(event) => {
                  event.stopPropagation();
                  setSelectedProjectId(params.row.projectId);
                }}
                chipTone={statusTone}
                chipIcon={<Box component={getStatusIconComponent(displayStatus)} />}
                chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
                chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
                durationLabel={durationLabel}
                durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
                timestampLabel={timestampLabel}
                timestampTitle={resolvedTimestampTitle}
                timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'output',
        headerName: getDetailLabel('output'),
        flex: columnWidths.output.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-meta-details-cell',
        renderCell: (params) => (
          <ExecutionOutputTableCell
            output={buildTestRunOutputValues({
              failureStack: params.row.failureStack,
              systemOut: params.row.systemOut,
              systemErr: params.row.systemErr,
            })}
          />
        ),
      },
      {
        field: 'details',
        headerName: getDetailLabel('details'),
        flex: columnWidths.details.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-meta-details-cell',
        renderCell: (params) => {
          const uploadedLabel = formatRelativeTime(params.row.createdAt);
          const uploadedTitle = formatDate(params.row.createdAt, { includeTime: true });
          const resultsFileLabel =
            params.row.resultsFile?.fileName ?? params.row.resultsFileId ?? '-';
          const resultsFileHref = buildResultsFileDetailPath(params.row.resultsFileId);
          return (
            <TableCellContainer gap="none">
              <TableBreakdownStack>
                <TableBreakdownLine
                  rowTitle="Execution Run"
                  icon={
                    <PlayCircleFilledWhiteOutlinedIcon
                      sx={{ fontSize: (theme) => theme.app.iconSizes.sm }}
                    />
                  }
                  href={buildExecutionRunDetailPath(params.row.runId)}
                  disableLinks={disableLinks}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  linkSx={EXECUTION_DETAILS_LINK_SX}
                  content={params.row.runId}
                />
                <TableBreakdownLine
                  rowTitle="Results File"
                  icon={
                    <DescriptionOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  }
                  content={resultsFileLabel}
                  href={resultsFileHref}
                  disableLinks={disableLinks}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  linkSx={EXECUTION_DETAILS_LINK_SX}
                />
                <TableBreakdownLine
                  rowTitle="Uploaded"
                  icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={uploadedLabel || '-'}
                  valueTitle={uploadedTitle || ''}
                  contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
                  valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
                />
              </TableBreakdownStack>
            </TableCellContainer>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        flex: columnWidths.actions.flex,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <TableActionsCell
            rowName={
              params.row.displayName ?? params.row.testName ?? params.row.name ?? params.row.id
            }
            editActionLabel={getEntityActionLabel('edit', 'Test Run')}
            deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
            onEdit={() => {
              setEditTarget(params.row);
            }}
            onDelete={() => {
              onDeleteRow(params.row);
            }}
            disableEdit={
              editEditor.isPending ||
              deleteRowPendingId === params.row.id ||
              updateRowTagsColorPendingId === params.row.id
            }
            disableDelete={deleteRowPendingId === params.row.id}
          />
        ),
      },
    ],
    [
      deleteRowPendingId,
      disableLinks,
      editEditor.isPending,
      hideSuiteNameInTestColumn,
      mergedTagColorByName,
      onDeleteRow,
      setSelectedProjectId,
      updateRowTagsColorPendingId,
    ],
  );
  const resolvedColumns = useMemo(() => {
    const visibleColumns = columns.filter((column) => {
      if (hideNameColumn && column.field === 'name') return false;
      if (hideDetailsColumn && column.field === 'details') return false;
      return true;
    });
    if (!runColumnFirst) {
      return visibleColumns;
    }
    const runColumn = visibleColumns.find((column) => column.field === 'runNumber');
    const remainingColumns = visibleColumns.filter((column) => column.field !== 'runNumber');
    return runColumn ? [runColumn, ...remainingColumns] : visibleColumns;
  }, [columns, hideDetailsColumn, hideNameColumn, runColumnFirst]);
  const togglableColumnFields = useMemo(
    () => resolvedColumns.map((column) => column.field).filter((field) => field !== 'name'),
    [resolvedColumns],
  );
  const gridConfig = testRunsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'test-runs-table' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });
  const gridProps = buildTableGridProps<TestRunDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
    showToolbar,
    chrome: 'embedded',
  });
  return (
    <>
      <ServerDataTable<TestRunDto>
        initialData={initialData}
        queryPath="test-runs/query"
        {...gridConfig}
        {...gridProps}
        columns={resolvedColumns}
        getRowId={(row) => row.id}
        isAuthenticated={Boolean(session?.userId)}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        onQueryMeta={onQueryMeta}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        showPaginationSkeletonWhenRowsVisible={showPaginationSkeletonWhenRowsVisible}
        autoHeight
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="status"]': {
              alignItems: 'flex-start',
            },
            '& .MuiDataGrid-cell[data-field="runNumber"], & .MuiDataGrid-cell[data-field="runId"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
          },
        ]}
      />
      <TestRunEditDialog
        open={editTarget !== null}
        execution={editTarget}
        loading={editEditor.isPending}
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await editEditor.save({ tags, colors, status, timestamp, durationMs });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};

export const TestRunsTable = memo(TestRunsTableComponent);
